import { ComponentRef } from '@opten/compiler/metadata/di';
import { OnInit } from '@opten/compiler/metadata/lifecycle';
import hasClass from '@opten/dom/has-class';
import isNullOrEmpty from '@opten/string/is-null-or-empty';
import { BasketProductItem, BasketProductVariantItem } from '../models/basket';
import { Location } from '../models/customer';
import { ShopContext } from '../shop/shop-context';
import select, { Select } from '@opten/select';

export interface BaseBasketOptions {
	selectors?: {
		location: string;
		product: string;
		productImage: string;
		productVariant: string;
		productVariantQuantity: string;
		productVariantPrice: string;
	};
	target?: string;
	id?: string;
	name?: string;
	disabled?: string;
}

interface SelectIdElement {
	id: string;
	select: Select;
}

export abstract class BaseBasket implements OnInit {
	abstract onInit(): void;

	abstract options: BaseBasketOptions;

	basketElement: HTMLElement;
	@ComponentRef(ShopContext) shopContext: ShopContext;
	constructor(private element: HTMLElement) {
		this.basketElement = element;
	}

	protected convertProduct(product: HTMLElement, convertForAddBulk = false): BasketProductItem {
		let productItem: BasketProductItem = {};
		const productVariants = Array.from(product.getElementsByClassName(this.options.selectors.productVariant));
		if (productVariants && productVariants.length > 0) {
			const productId = this.getId(product);
			if (isNullOrEmpty(productId) === false) {
				const productName = this.getName(product);
				const orderType = this.getOrderType(product);
				const productVariantsItems: BasketProductVariantItem[] = [];
				productVariants.forEach(productVariant => {
					const productVariantQuantity = productVariant.getElementsByClassName(
						this.options.selectors.productVariantQuantity
					)[0] as HTMLInputElement;
					let quantity = 0;
					if (productVariantQuantity && productVariantQuantity.disabled !== true) {
						quantity = Number(
							productVariantQuantity.tagName.toLowerCase() === 'input'
								? productVariantQuantity.value
								: productVariantQuantity.innerText
						);

						if ((isNaN(quantity) || (convertForAddBulk && quantity < 1)) === false) {
							const productVariantId = this.getId(productVariant);
							if (isNullOrEmpty(productVariantId) === false) {
								const productVariantName = this.getName(productVariant);
								const productVariantAmountUomId = this.getAmountUomId(productVariant);
								const productVariantArticleNumber = this.getArticleNumber(productVariant);
								const productVariantsItem: BasketProductVariantItem = {
									id: productVariantId,
									articleNumber: productVariantArticleNumber,
									name: productVariantName,
									amountUomId: productVariantAmountUomId,
									umbracoId: productVariant.getAttribute('data-umbraco-id'),
									quantity
								};
								productVariantsItems.push(productVariantsItem);
							}
						}
					}
				});
				if (productVariantsItems.length > 0) {
					const productImage = product.getElementsByClassName(this.options.selectors.productImage)[0] as HTMLElement;
					productItem = {
						id: productId,
						name: productName,
						variants: productVariantsItems,
						orderType,
						image: productImage ? productImage.innerHTML : '',
						isNew: product.querySelector('.c-badge__container.--small') != null,
						brand: product.getAttribute('data-brand'),
						umbracoId: product.getAttribute('data-umbraco-id'),
						isPromotionPackage: false
					};
				}
			}
		}
		return productItem;
	}

	protected convertPromotionPackage(product: HTMLElement): BasketProductItem {
		let productItem: BasketProductItem = null;
		const productVariants = Array.from(product.getElementsByClassName(this.options.selectors.productVariant));
		const master = product.querySelector('.js-promotion-package');

		if (master && productVariants && productVariants.length > 0) {
			const masterQuantityElement = master.querySelector(`.${this.options.selectors.productVariantQuantity}`) as HTMLInputElement;
			const masterQuantity = Number(
				masterQuantityElement.tagName.toLowerCase() === 'input' ? masterQuantityElement.value : masterQuantityElement.innerText
			);
			const productId = this.getId(product);
			if (isNullOrEmpty(productId) === false) {
				const productName = this.getName(product);
				const orderType = this.getOrderType(product);
				const productVariantsItems: BasketProductVariantItem[] = [];
				productVariants.forEach(productVariant => {
					const productVariantQuantity = productVariant.getElementsByClassName(
						this.options.selectors.productVariantQuantity
					)[0] as HTMLInputElement;
					let quantity = 0;
					if (productVariantQuantity) {
						quantity = Number(
							productVariantQuantity.tagName.toLowerCase() === 'input'
								? productVariantQuantity.value
								: productVariantQuantity.innerText
						);

						quantity = quantity * masterQuantity;

						if (isNaN(quantity) === false) {
							const productVariantId = this.getId(productVariant);
							if (isNullOrEmpty(productVariantId) === false) {
								const productVariantName = this.getName(productVariant);
								const productVariantAmountUomId = this.getAmountUomId(productVariant);
								const productVariantArticleNumber = this.getArticleNumber(productVariant);
								const productVariantsItem: BasketProductVariantItem = {
									id: productVariantId,
									articleNumber: productVariantArticleNumber,
									name: productVariantName,
									amountUomId: productVariantAmountUomId,
									quantity
								};
								productVariantsItems.push(productVariantsItem);
							}
						}
					}
				});

				if (productVariantsItems.length > 0) {
					const productImage = product.getElementsByClassName(this.options.selectors.productImage)[0] as HTMLElement;
					productItem = {
						id: productId,
						name: productName,
						variants: productVariantsItems,
						orderType,
						image: productImage ? productImage.innerHTML : '',
						isNew: product.querySelector('.c-badge__container.--small') != null,
						brand: product.getAttribute('data-brand'),
						umbracoId: product.getAttribute('data-umbraco-id'),
						isPromotionPackage: true,
						quantity: masterQuantity
					};
				}
			}
		}
		return productItem;
	}

	protected getLocation(wrapperElement: HTMLElement): Location {
		let location: Location = null;
		const locationElement = hasClass(wrapperElement, this.options.selectors.location)
			? wrapperElement
			: (wrapperElement.getElementsByClassName(this.options.selectors.location)[0] as HTMLElement);

		if (locationElement) {
			const locationId = locationElement.getAttribute(this.options.id);
			if (isNullOrEmpty(locationId) === false) {
				location = {
					id: +locationId
				};
			}
		}
		return location;
	}

	protected reinitLocationSelect(wrapperElement: HTMLElement, selectElement: HTMLSelectElement): HTMLSelectElement {
		const newSelectElementHtml = selectElement.outerHTML;
		const selectParentElement = selectElement.parentElement;
		let newSelectElement = null;

		if (selectElement != null && selectParentElement != null) {
			const selectLabel = selectParentElement.lastElementChild;
			const selectLabelHtml =
				selectLabel !== undefined && selectLabel && selectLabel.tagName.toLowerCase() === 'label' ? selectLabel.outerHTML : '';
			selectParentElement.innerHTML = newSelectElementHtml + selectLabelHtml;
			newSelectElement = selectParentElement.firstChild;

			select(newSelectElement as HTMLSelectElement);
			wrapperElement.removeEventListener('finished.opten.renderer', () => {
				selectElement = this.reinitLocationSelect(wrapperElement, selectElement);
			});
		}

		return newSelectElement != null ? newSelectElement : selectElement;
	}

	private getId(element: Element): string {
		return element.getAttribute(this.options.id);
	}
	private getName(element: Element): string {
		return element.getAttribute(this.options.name);
	}
	private getOrderType(element: Element): string {
		return element.getAttribute('data-order-type');
	}

	private getAmountUomId(element: Element): string {
		return element.getAttribute('data-amount-uom-id');
	}

	private getArticleNumber(element: Element): string {
		return element.getAttribute('data-article-number');
	}
}
